/**
 * Device-local draft buffer for the hot-path screens.
 *
 * Spec, "UX rules that decide whether this beats paper":
 *   "Draft auto-saves to the device as you type, so a signal blip, a locked
 *    screen or a dead battery never loses a part-filled record."
 * and, "Error handling":
 *   "Failed save: the draft stays on the device and the UI shows an unsaved
 *    state with a retry. Data is never discarded because a request failed."
 *
 * Deliberately tiny and total: localStorage can throw (Safari private mode,
 * quota) and a QC person mid-pour must never see a crash because of it. Every
 * failure degrades to "no local buffer", never to a thrown error.
 */

const PREFIX = 'sci.draft.';

/** Read a draft. Returns null when absent, unreadable or corrupt. */
export function loadDraft<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    return raw === null ? null : (JSON.parse(raw) as T);
  } catch {
    return null;
  }
}

/** Write a draft. Silently does nothing if storage is unavailable or full. */
export function saveDraft(key: string, value: unknown): void {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    /* no local buffer available; the in-memory draft still holds */
  }
}

/** Drop a draft once everything in it has been confirmed by the server. */
export function clearDraft(key: string): void {
  try {
    localStorage.removeItem(PREFIX + key);
  } catch {
    /* nothing to do */
  }
}
